<template>
    <div class="avg-calls-duration-form">
        <div class="w-full">
            <VcFormItem
                :label="t('widget.countersToDisplay')"
            >
                <VcSelect
                    v-model="widgetLayoutModel.showCounter"
                    :options="availableCountersOptions"
                    :config="{
                        valueKey: 'value',
                        labelKey: 'label'
                    }"
                    class="w-full"
                />
            </VcFormItem>
        </div>
        <div>
            <VcFormItem
                :label="t('widget.contentTitle')"
            >
                <VcInput v-model="contentTitleModel" />
            </VcFormItem>
        </div>
        <div class="mb-5 space-y-1.5">
            <VcCheckbox
                v-model="widgetLayoutModel.showText"
            >
                {{ t('status.show.text') }}
            </VcCheckbox>
            <VcCheckbox
                v-model="widgetLayoutModel.displayBorder"
            >
                {{ t('status.display.border') }}
            </VcCheckbox>
        </div>
        <div>
            <WidgetInnerAlignment
                :content="widgetContentAlignment"
                :layout="widgetContentLayout"
                @update:content="$evt => widgetContentAlignment = $evt"
                @update:layout="$evt => widgetContentLayout = $evt"
            />
        </div>
        <div>
            <WidgetCardLayoutConfigs v-model="widgetLayoutModel" />
        </div>
        <div v-if="widgetTime?.type">
            <VcCollapseItem
                :title="t('settings.time.frame')"
                name="widget-time-frame"
                class="simple-collapse"
                :has-border="false"
            >
                <RedesignWidgetTimeFrame
                    v-model="widgetTimeModel"
                    :time-frame-type="widgetTimeModel.type"
                    :widget-time-options="widgetTimeOptions"
                >
                    <template #frame-types>
                        <VcRadioButtonsGroup
                            v-model="widgetTimeModel.type"
                            :radios="createWidgetTimeTypes"
                            :config="{ valueKey: 'value', labelKey: 'label' }"
                            class="radio-groups mb-4"
                        />
                    </template>
                </RedesignWidgetTimeFrame>
            </VcCollapseItem>
        </div>
        <div v-if="widgetConfigMapped.filters.length">
            <VcCollapseItem
                :title="t('settings.filters')"
                name="filters"
                class="simple-collapse"
                :has-border="false"
            >
                <div
                    v-if="autoCompletes.length"
                    class="flex flex-col text-main-sm items-start main-text-color gap-2 mb-5"
                >
                    {{ t('tooltip.refresh.entities.list') }}
                    <RefreshButton
                        :disabled="loadEntitiesList"
                        :loading="loadEntitiesList"
                        @click="refreshEntitiesList"
                    />
                </div>
                <div
                    v-for="(filter, index) in widgetConfigMapped.filters"
                    :key="index"
                >
                    <RedesignWidgetConfigEntityTree
                        v-if="isAutoComplete(filter)"
                        :key="`filter-tree-${filter.ParameterID}`"
                        :value="getWidgetConfig(filter)"
                        class="mb-4"
                        @update:value="updateFilterItem"
                    />
                    <RedesignWidgetConfigSelect
                        v-if="isMultiSelect(filter)"
                        :key="`filter-select-${filter.ParameterID}`"
                        :value="getWidgetConfig(filter)"
                        @update:value="updateFilterItem"
                    />
                </div>
            </VcCollapseItem>
        </div>
        <div v-if="widgetConfigMapped.otherFilters.length">
            <VcCollapseItem
                :title="t('settings.other.filters')"
                name="otherFilters"
                class="simple-collapse"
                :has-border="false"
            >
                <div
                    v-for="(filter, index) in widgetConfigMapped.otherFilters"
                    :key="`other-filter-${index}`"
                    class="mb-4"
                >
                    <RedesignWidgetOtherFilters
                        :value="getWidgetConfig(filter)"
                        :parameter-type="filter.ParameterType"
                        @update:value="updateFilterItem"
                    />
                </div>
            </VcCollapseItem>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVModel } from '@vueuse/core'
import { VcFormItem, VcSelect, VcInput, VcCheckbox, VcCollapseItem, VcRadioButtonsGroup } from '@voicenter-team/voicenter-ui-plus'
import { WidgetConfig, WidgetLayout, WidgetTime } from '@/types/dashboard'
import { availableCounters, AVERAGE_DURATION_ALL_CALLS } from '@/enum/queueCounters'
import { widgetTimeOptions, widgetTimeTypes } from '@/enum/widgetTimeOptions'
import {
    getWidgetConfigByTemplateId,
    getWidgetConfigMapped,
    isAutoComplete,
    isMultiSelect
} from '@/components/RedesignComponents/Widgets/widget.helper'
import RedesignWidgetConfigSelect from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigSelect.vue'
import RedesignWidgetTimeFrame from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetTimeFrame.vue'
import RefreshButton from '@/components/RefreshButton.vue'
import RedesignWidgetOtherFilters from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetOtherFilters.vue'
import WidgetCardLayoutConfigs
    from '@/components/RedesignComponents/Widgets/Edit/WidgetCardsForms/WidgetCardLayoutConfigs.vue'
import WidgetInnerAlignment from '@/components/RedesignComponents/Widgets/Common/WidgetInnerAlignment.vue'
import RedesignWidgetConfigEntityTree
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigEntityTree.vue'

interface IProps {
    modelValue: WidgetLayout
    widgetTime: WidgetTime
    widgetConfig: WidgetConfig[]
    templateId: number
    loadEntitiesList?: boolean
}

const { t } = useI18n()

const props = defineProps<IProps>()
const emit = defineEmits<{
    (e: 'update:modelValue', pl: WidgetLayout): void
    (e: 'update:config', pl: WidgetConfig): void
    (e: 'refreshEntitiesList'): void
    (e: 'update:widgetTime', pl: WidgetTime): void
}>()


const widgetLayoutModel = useVModel(props, 'modelValue', emit, {
    passive: true
})
const showCounterKey = computed(() => {
    return widgetLayoutModel.value.showCounter || AVERAGE_DURATION_ALL_CALLS
})

const defaultContentTitle = computed(() => {
    const entry = availableCounters.find((counter) => counter.value === showCounterKey.value)
    return entry ? t(entry.label).toString() : showCounterKey.value
})

const widgetTimeModel = computed({
    get () {
        return props.widgetTime
    },
    set (val) {
        emit('update:widgetTime', val)
    }
})

const contentTitleModel = computed({
    get () {
        const key = showCounterKey.value
        const title = widgetLayoutModel.value?.ContentTitle

        if (title && (title[key] || title[key] === '')) {
            return title[key] as string
        }

        return defaultContentTitle.value
    },
    set (val) {
        const key = showCounterKey.value

        if (defaultContentTitle.value === val) {
            return
        }
        
        const ContentTitle = widgetLayoutModel.value.ContentTitle || {}

        widgetLayoutModel.value = {
            ...widgetLayoutModel.value,
            ContentTitle: {
                ...ContentTitle,
                [key]: String(val)
            }
        }
    }
})
const widgetContentAlignment = computed({
    get () {
        return widgetLayoutModel.value?.contentAlignment === undefined ? 'center' : widgetLayoutModel.value.contentAlignment
    },
    set (val) {
        if (widgetLayoutModel.value.contentAlignment === undefined) {
            widgetLayoutModel.value = {
                ...widgetLayoutModel.value,
                contentAlignment: 'center'
            }
        }
        widgetLayoutModel.value.contentAlignment = val
    }
})
const widgetContentLayout = computed({
    get () {
        return widgetLayoutModel.value?.contentLayout === undefined ? 'default' : widgetLayoutModel.value.contentLayout
    },
    set (val) {
        if (widgetLayoutModel.value.contentLayout === undefined) {
            widgetLayoutModel.value = {
                ...widgetLayoutModel.value,
                contentLayout: 'default'
            }
        }
        widgetLayoutModel.value.contentLayout = val
    }
})
const widgetConfigMapped = computed(() => {
    return getWidgetConfigMapped(props.widgetConfig)
})
const availableCountersOptions = computed(() => {
    return availableCounters.map(el => {
        return {
            label: t(el.label),
            value: el.value
        }
    })
})
const autoCompletes = computed(() => {
    return props.widgetConfig.filter(config => isAutoComplete(config))
})
const createWidgetTimeTypes = computed(() => {
    return widgetTimeTypes
        .map(el => {
            return {
                label: t(el.text),
                value: el.label
            }
        })
})

/* Methods */
function getWidgetConfig (config: WidgetConfig) {
    return getWidgetConfigByTemplateId(config, props.templateId)
}

function updateFilterItem (filterData: WidgetConfig) {
    emit('update:config', filterData)
}

function refreshEntitiesList () {
    emit('refreshEntitiesList')
}
</script>
